import { useCallback, useEffect, useRef, useState } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Dialog, Divider, IconButton, LinearProgress, Menu, MenuItem,
  NativeSelect, Paper, TextField, Tooltip, Typography, useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useGlobalStore } from '../../stores/globalStore';
import { useNotificationStore } from '../../stores/notificationStore';
import { getReportExportBlob, getReportPdfBlob, type ReportExportFormat } from '../../api/reportsApi';
import { saveFile } from '../../utils/saveFile';
import { canPrintPdfInPage, printPdfBlob } from '../../utils/printPdf';
import { markReportPrinted } from '../../pages/reports/readyReports';
import { usePdfViewer } from './usePdfViewer';
import { PDFJS_SCOPE_CLASS } from './pdfViewerCss';
import { readSavedZoom, reportFileName, saveZoom, trackViewerAction as track, ZOOM_OPTIONS } from './reportViewerSettings';
import './ReportPdfDialog.css';

const EXPORT_LABEL: Record<ReportExportFormat, string> = { xlsx: 'Excel', docx: 'Word', csv: 'CSV' };

function loadErrorMessage(e: unknown): string {
  if (axios.isAxiosError(e) && e.response?.status === 404) {
    return 'This report could not be found. It may have been deleted.';
  }
  return 'The report could not be loaded.';
}

/** An icon button whose tooltip text is its accessible name. */
function ToolButton({ label, onClick, disabled, children, pressed }: {
  label: string; onClick: () => void; disabled?: boolean; children: ReactNode; pressed?: boolean;
}) {
  return (
    <Tooltip title={label}>
      {/* span: a disabled button fires no events, so the tooltip needs a wrapper */}
      <span>
        <IconButton
          size="small"
          aria-label={label}
          aria-pressed={pressed}
          onClick={onClick}
          disabled={disabled}
          sx={{ borderRadius: 1, p: '5px' }}
        >
          {children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function ToolbarDivider() {
  return <Divider orientation="vertical" flexItem sx={{ mx: 0.75, my: 0.5 }} />;
}

interface Props {
  open: boolean;
  guid: string | null;
  reportName?: string;
  /** tblRS_ReportDocument key. When given, Print marks the report Printed in the Print Queue. */
  reportDocumentId?: number | null;
  onClose: () => void;
}

/**
 * The report viewer: the stored report's PDF (GET api/report/Pdf, the PRNX
 * the API renders) shown with pdf.js. It fills the window over the screen
 * that opened it, as an item does over its grid (ItemEditorDialog), and the
 * back arrow at the top left returns there. Pages scroll continuously with a
 * selectable text layer; the toolbar has page navigation, zoom, Print, Export
 * (PDF, Excel, Word) and Search.
 */
export default function ReportPdfDialog({ open, guid, reportName, reportDocumentId, onClose }: Props) {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const addNotification = useNotificationStore((s) => s.addNotification);
  // MUI's sm breakpoint (600px): phones get the whole screen.
  const isPhone = useMediaQuery('(max-width:599.95px)');

  const [pdf, setPdf] = useState<Blob | null>(null);
  const pdfRequest = useRef<Promise<Blob> | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  // Page Width unless the user picked a zoom from the list; read again for each report.
  const [initialZoom, setInitialZoom] = useState(readSavedZoom);

  const [findOpen, setFindOpen] = useState(false);
  const [findQuery, setFindQuery] = useState('');
  const findInputRef = useRef<HTMLInputElement | null>(null);
  const [findFocus, setFindFocus] = useState(0);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [exporting, setExporting] = useState<ReportExportFormat | null>(null);
  const [printing, setPrinting] = useState(false);
  const [pageDraft, setPageDraft] = useState<string | null>(null);

  // Object URLs made for downloads and new tabs; revoked after a minute or on close.
  const objectUrls = useRef(new Map<string, number>());
  const makeObjectUrl = useCallback((blob: Blob) => {
    const url = URL.createObjectURL(blob);
    const timer = window.setTimeout(() => {
      URL.revokeObjectURL(url);
      objectUrls.current.delete(url);
    }, 60_000);
    objectUrls.current.set(url, timer);
    return url;
  }, []);
  const revokeObjectUrls = useCallback(() => {
    objectUrls.current.forEach((timer, url) => {
      window.clearTimeout(timer);
      URL.revokeObjectURL(url);
    });
    objectUrls.current.clear();
  }, []);
  useEffect(() => revokeObjectUrls, [revokeObjectUrls]);

  // Fetch the PDF when the dialog opens on a report. Closing keeps it, so the
  // pages are still there while the dialog fades out; a new report replaces it.
  useEffect(() => {
    if (!guid) {
      setPdf(null);
      pdfRequest.current = null;
      return;
    }
    if (!open) return;
    let cancelled = false;
    setPdf(null);
    setLoadError(null);
    const request = getReportPdfBlob(facilityId, guid);
    pdfRequest.current = request;
    request.then(
      (blob) => { if (!cancelled) setPdf(blob); },
      (e: unknown) => { if (!cancelled) setLoadError(loadErrorMessage(e)); },
    );
    // Keep an unhandled rejection off the console; the branch above reports it.
    request.catch(() => {});
    return () => { cancelled = true; };
  }, [open, guid, facilityId, attempt]);

  // A different report (or none) starts with find closed, no page typed,
  // at the remembered zoom.
  useEffect(() => {
    setInitialZoom(readSavedZoom());
    setFindOpen(false);
    setFindQuery('');
    setPageDraft(null);
  }, [guid]);

  const viewer = usePdfViewer({
    pdf,
    container,
    initialScaleValue: initialZoom,
    onError: () => setLoadError('The report could not be shown.'),
  });

  const ready = viewer.status === 'ready' && !loadError;
  const loading = !!guid && !loadError && !ready;

  const handleClose = useCallback(() => {
    setFindOpen(false);
    setExportAnchor(null);
    revokeObjectUrls();
    onClose();
  }, [revokeObjectUrls, onClose]);

  // ---- find
  const openFind = useCallback(() => {
    if (!findOpen) track('find');
    setFindOpen(true);
    setFindFocus((n) => n + 1);
  }, [findOpen]);
  // Focus (and select) the find box each time it is asked for, once it has rendered.
  useEffect(() => {
    if (findFocus === 0) return;
    findInputRef.current?.focus();
    findInputRef.current?.select();
  }, [findFocus]);
  const closeFind = useCallback(() => {
    setFindOpen(false);
    viewer.clearSearch();
    container?.focus();
  }, [viewer, container]);

  const handleDialogClose = useCallback((_e: unknown, reason?: 'backdropClick' | 'escapeKeyDown') => {
    // Esc closes the find box first, then the dialog.
    if (reason === 'escapeKeyDown' && findOpen) {
      closeFind();
      return;
    }
    handleClose();
  }, [findOpen, closeFind, handleClose]);

  const handleDialogKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if ((e.ctrlKey || e.metaKey) && !e.altKey && e.key.toLowerCase() === 'f') {
      e.preventDefault();
      // Keep it from the app's window-level Ctrl+F (TopToolbar's global search),
      // which would open over the report.
      e.stopPropagation();
      if (ready) openFind();
    }
  }, [ready, openFind]);

  const handleDocumentKeyDown = useCallback((e: KeyboardEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget || e.altKey || e.ctrlKey || e.metaKey) return;
    const act = ({
      PageDown: viewer.nextPage,
      PageUp: viewer.previousPage,
      Home: viewer.firstPage,
      End: viewer.lastPage,
    } as Record<string, (() => void) | undefined>)[e.key];
    if (!act) return;
    e.preventDefault();
    act();
  }, [viewer]);

  // ---- open, print, download
  const openPdfInNewTab = useCallback((blob: Blob) => {
    const url = makeObjectUrl(blob);
    window.open(url, '_blank', 'noopener');
  }, [makeObjectUrl]);


  const handlePrint = useCallback(async () => {
    if (!pdf) return;
    track('print');
    // They hit Print: the Print Queue shows it Printed, Viewed or not.
    const markPrinted = () => {
      if (reportDocumentId) void markReportPrinted(facilityId, reportDocumentId, userId);
    };
    // Phones, tablets and browsers set to download PDFs can't print from a hidden
    // frame: give them the PDF in a tab, where their own Print works.
    if (!canPrintPdfInPage()) {
      openPdfInNewTab(pdf);
      markPrinted();
      return;
    }
    setPrinting(true);
    try {
      await printPdfBlob(pdf);
      markPrinted();
    } catch {
      addNotification({
        message: 'Printing did not start. Open the PDF and print it from there.',
        type: 'warning',
        action: { label: 'Open as PDF', handler: () => openPdfInNewTab(pdf) },
      });
    } finally {
      setPrinting(false);
    }
  }, [pdf, openPdfInNewTab, addNotification, reportDocumentId, facilityId, userId]);

  const saveBlob = useCallback((blob: Blob, fileName: string) => {
    const url = makeObjectUrl(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [makeObjectUrl]);

  // Where the browser has a Save As box (Chrome, Edge), the user picks the
  // folder and name; elsewhere it downloads. A save to a picked folder shows no
  // download bar, so say where it went.
  const save = useCallback(async (fileName: string, source: Blob | (() => Promise<Blob>)) => {
    const result = await saveFile(fileName, source, { download: saveBlob });
    if (result.outcome === 'saved') addNotification(`Saved ${result.name}`, 'success');
  }, [saveBlob, addNotification]);

  // The title's Download PDF: saved under the report's name. It used to open
  // the PDF in a new tab from a blob URL, and the browser saved that under a
  // random name. Still loading: saved as soon as it arrives.
  const handleDownloadFromTitle = useCallback(() => {
    track('download-pdf');
    const name = reportFileName(reportName, 'pdf');
    const request = pdfRequest.current;
    if (!pdf && !request) return;
    save(name, pdf ?? (() => request!))
      .catch(() => addNotification('The report could not be downloaded. Try again.', 'error'));
  }, [pdf, reportName, save, addNotification]);

  const handleDownloadPdf = useCallback(() => {
    setExportAnchor(null);
    if (!pdf) return;
    track('pdf');
    void save(reportFileName(reportName, 'pdf'), pdf);
  }, [pdf, reportName, save]);

  const handleExport = useCallback(async (format: ReportExportFormat) => {
    setExportAnchor(null);
    if (!guid) return;
    track(format);
    try {
      // Built after the Save As box closes, so the spinner doesn't run behind it.
      await save(reportFileName(reportName, format), () => {
        setExporting(format);
        return getReportExportBlob(facilityId, guid, format);
      });
    } catch (e) {
      // 422: a report with no table has nothing to put in a CSV.
      addNotification(axios.isAxiosError(e) && e.response?.status === 422
        ? `This report has no table to save as ${EXPORT_LABEL[format]}. Export it to Excel instead.`
        : `The report could not be exported to ${EXPORT_LABEL[format]}. Try again.`, 'error');
    } finally {
      setExporting(null);
    }
  }, [guid, facilityId, reportName, save, addNotification]);

  // ---- page box
  const commitPageDraft = useCallback(() => {
    if (pageDraft == null) return;
    const n = Number(pageDraft);
    if (Number.isFinite(n) && pageDraft.trim() !== '') viewer.goToPage(n);
    setPageDraft(null);
  }, [pageDraft, viewer]);

  const zoomOptions = ZOOM_OPTIONS.some((o) => o.value === viewer.scaleValue)
    ? ZOOM_OPTIONS
    : [...ZOOM_OPTIONS, { value: viewer.scaleValue, label: `${Math.round(viewer.scale * 100)}%` }];

  const findStatus = !findQuery
    ? ''
    : viewer.find.notFound
      ? 'No matches'
      : viewer.find.total > 0
        ? `${viewer.find.current.toLocaleString('en-US')} of ${viewer.find.total.toLocaleString('en-US')}`
        : '';

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      fullScreen
      aria-labelledby="report-pdf-dialog-title"
      // On the dialog root: focus may sit on MUI's container, outside the paper.
      onKeyDown={handleDialogKeyDown}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        },
      }}
    >
      {/* Title row: the report's name; Download PDF and the close X on the right,
          as on every other dialog. Its padding is in ReportPdfDialog.css (phone
          gutter, safe areas). */}
      <Box
        className={`report-pdf-titlebar${isPhone ? ' report-pdf-titlebar--phone' : ''}`}
        sx={{
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          flexWrap: isPhone ? 'wrap' : 'nowrap',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <Typography
          id="report-pdf-dialog-title"
          noWrap
          sx={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600 }}
        >
          {reportName || 'Report Preview'}
        </Typography>
        {guid && (
          // On a phone the buttons take a row of their own under the title.
          <Box className="report-pdf-title-actions" sx={{ display: 'flex', alignItems: 'center', gap: 1, order: isPhone ? 3 : 0, ...(isPhone ? { width: '100%', justifyContent: 'flex-end' } : {}) }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleDownloadFromTitle}
              startIcon={<i className="bi bi-file-earmark-arrow-down" />}
              sx={{ textTransform: 'none', fontSize: 12, flexShrink: 0 }}
            >
              Download PDF
            </Button>
          </Box>
        )}
        {/* The X stays on the title's line on a phone; Download PDF wraps under. */}
        <Tooltip title="Close">
          <IconButton size="small" onClick={handleClose} aria-label="Close" sx={{ color: 'text.secondary', padding: '4px', order: isPhone ? 2 : 0 }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Toolbar, centred in a grey band. */}
      {guid && (
        <Box
          role="toolbar"
          aria-label="Report"
          sx={{
            bgcolor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[100]),
            borderBottom: 1,
            borderColor: 'divider',
            px: 1,
            py: 0.25,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'center',
            rowGap: 0.25,
          }}
        >
          <ToolButton label="First page" onClick={viewer.firstPage} disabled={!ready || viewer.pageNumber <= 1}>
            <FirstPageIcon fontSize="small" />
          </ToolButton>
          <ToolButton label="Previous page" onClick={viewer.previousPage} disabled={!ready || viewer.pageNumber <= 1}>
            <NavigateBeforeIcon fontSize="small" />
          </ToolButton>
          {ready ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mx: 0.5 }}>
              <Tooltip title="Page">
                <TextField
                  size="small"
                  value={pageDraft ?? String(viewer.pageNumber)}
                  onChange={(e) => setPageDraft(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={commitPageDraft}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitPageDraft();
                    } else if (e.key === 'Escape' && pageDraft != null) {
                      e.stopPropagation();
                      setPageDraft(null);
                    }
                  }}
                  inputProps={{
                    'aria-label': 'Page',
                    inputMode: 'numeric',
                    style: { textAlign: 'center', padding: '3px 4px', fontSize: 13, width: 36 },
                  }}
                  sx={{ bgcolor: 'background.paper' }}
                />
              </Tooltip>
              <Typography sx={{ fontSize: 13, whiteSpace: 'nowrap' }}>
                of {viewer.pagesCount.toLocaleString('en-US')}
              </Typography>
            </Box>
          ) : (
            <Typography sx={{ fontSize: 13, mx: 1, color: 'text.secondary', whiteSpace: 'nowrap' }}>0 pages</Typography>
          )}
          <ToolButton label="Next page" onClick={viewer.nextPage} disabled={!ready || viewer.pageNumber >= viewer.pagesCount}>
            <NavigateNextIcon fontSize="small" />
          </ToolButton>
          <ToolButton label="Last page" onClick={viewer.lastPage} disabled={!ready || viewer.pageNumber >= viewer.pagesCount}>
            <LastPageIcon fontSize="small" />
          </ToolButton>

          <ToolbarDivider />

          {!isPhone && (
            <ToolButton label="Zoom out" onClick={viewer.zoomOut} disabled={!ready}>
              <ZoomOutIcon fontSize="small" />
            </ToolButton>
          )}
          <Tooltip title="Zoom">
            {/* span: MUI blurs a focused input when it is disabled (a new report loading)
                by calling onBlur() with no event, which crashes the Tooltip's own handler. */}
            <span>
              <NativeSelect
                value={viewer.scaleValue}
                onChange={(e) => {
                  viewer.setScaleValue(e.target.value);
                  // Only a pick from this list is remembered; Zoom in/out are for the moment.
                  saveZoom(e.target.value);
                }}
                disabled={!ready}
                disableUnderline
                inputProps={{ 'aria-label': 'Zoom' }}
                sx={{
                  fontSize: 13,
                  mx: 0.5,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 1,
                  '& select': { py: '3px', pl: 1, minWidth: 92, fontSize: 13 },
                }}
              >
                {zoomOptions.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </NativeSelect>
            </span>
          </Tooltip>
          {!isPhone && (
            <ToolButton label="Zoom in" onClick={viewer.zoomIn} disabled={!ready}>
              <ZoomInIcon fontSize="small" />
            </ToolButton>
          )}

          <ToolbarDivider />

          <ToolButton label="Print" onClick={() => { void handlePrint(); }} disabled={!pdf || printing}>
            <PrintOutlinedIcon fontSize="small" />
          </ToolButton>
          <Tooltip title="Export">
            <span>
              <IconButton
                size="small"
                aria-label="Export"
                aria-haspopup="menu"
                aria-expanded={exportAnchor ? 'true' : undefined}
                onClick={(e) => setExportAnchor(e.currentTarget)}
                disabled={exporting != null}
                sx={{ borderRadius: 1, p: '5px' }}
              >
                <FileDownloadOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Menu
            anchorEl={exportAnchor}
            open={!!exportAnchor}
            onClose={() => setExportAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            MenuListProps={{ dense: true }}
          >
            <MenuItem onClick={handleDownloadPdf} disabled={!pdf}>PDF</MenuItem>
            <MenuItem onClick={() => { void handleExport('xlsx'); }}>Excel</MenuItem>
            <MenuItem onClick={() => { void handleExport('docx'); }}>Word</MenuItem>
            <MenuItem onClick={() => { void handleExport('csv'); }}>CSV</MenuItem>
          </Menu>

          <ToolbarDivider />

          <ToolButton label="Search" onClick={openFind} disabled={!ready} pressed={findOpen}>
            <SearchIcon fontSize="small" />
          </ToolButton>
        </Box>
      )}

      {/* Pages: grey surround, white pages with a light shadow. */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          bgcolor: (t) => (t.palette.mode === 'dark' ? t.palette.grey[800] : '#e4e4e4'),
          minHeight: 0,
        }}
      >
        {(loading || exporting) && (
          <LinearProgress
            aria-label={exporting ? `Exporting to ${EXPORT_LABEL[exporting]}` : 'Loading report'}
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 3 }}
          />
        )}
        {!guid && (
          <Typography sx={{ p: 2, color: 'text.secondary' }}>No report selected.</Typography>
        )}
        {loadError && (
          <Alert
            severity="error"
            sx={{ position: 'absolute', top: 16, left: 16, right: 16, zIndex: 3 }}
            action={(
              <Button color="inherit" size="small" onClick={() => { setLoadError(null); setAttempt((a) => a + 1); }}>
                Retry
              </Button>
            )}
          >
            {loadError}
          </Alert>
        )}
        {guid && (
          <Box
            ref={setContainer}
            // pdf.js's stylesheet is scoped to PDFJS_SCOPE_CLASS (pdfViewerCss.ts);
            // report-pdf-pages is the wider scroll bar and the safe-area inset
            // (ReportPdfDialog.css).
            className={`${PDFJS_SCOPE_CLASS} report-pdf-pages`}
            tabIndex={0}
            role="region"
            aria-label="Report pages"
            data-testid="report-pdf-pages"
            onKeyDown={handleDocumentKeyDown}
            sx={{
              // pdf.js requires an absolutely positioned scroll container.
              // Its edges (inset) are in ReportPdfDialog.css.
              position: 'absolute',
              overflow: 'auto',
              outline: 'none',
              '&:focus-visible': { boxShadow: (t) => `inset 0 0 0 2px ${t.palette.primary.main}` },
              '--page-margin': '10px auto',
              '--page-border': '1px solid transparent',
              '& .pdfViewer .page': {
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
                backgroundColor: '#fff',
              },
            }}
          />
        )}
        {findOpen && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              top: 8,
              right: isPhone ? 8 : 24,
              left: isPhone ? 8 : 'auto',
              zIndex: 4,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              p: 0.5,
              pl: 1,
            }}
          >
            <TextField
              size="small"
              placeholder="Find"
              value={findQuery}
              inputRef={findInputRef}
              onChange={(e) => {
                setFindQuery(e.target.value);
                viewer.search(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  viewer.search(findQuery, { again: true, previous: e.shiftKey });
                }
              }}
              sx={{ flex: isPhone ? 1 : 'none', minWidth: 0 }}
              inputProps={{ 'aria-label': 'Find', style: { fontSize: 13, padding: '4px 8px', width: isPhone ? '100%' : 160 } }}
            />
            <Typography
              role="status"
              sx={{ fontSize: 12, color: viewer.find.notFound ? 'error.main' : 'text.secondary', minWidth: 64, textAlign: 'center' }}
            >
              {findStatus}
            </Typography>
            <ToolButton
              label="Previous match"
              onClick={() => viewer.search(findQuery, { again: true, previous: true })}
              disabled={!findQuery}
            >
              <KeyboardArrowUpIcon fontSize="small" />
            </ToolButton>
            <ToolButton
              label="Next match"
              onClick={() => viewer.search(findQuery, { again: true })}
              disabled={!findQuery}
            >
              <KeyboardArrowDownIcon fontSize="small" />
            </ToolButton>
            <ToolButton label="Close find" onClick={closeFind}>
              <CloseIcon fontSize="small" />
            </ToolButton>
          </Paper>
        )}
      </Box>
    </Dialog>
  );
}
